/**
 * Permission helper utilities
 * SINGLE SOURCE OF TRUTH for permission checks
 */

import { PermissionsData } from '@/lib/platform/navigation/get-first-accessible-page';

/**
 * Check if user has access to ANY valuation feature
 * Used for: Navigation tree filtering, page access, feature visibility
 *
 * Returns true if user has:
 * - Dashboard role
 * - Sales permission
 * - Transfer permission
 * - Marketing permission
 * - Brand permission
 * - Admin role
 */
export function hasAnyValuationPermission(
  permissions: PermissionsData | null | undefined
): boolean {
  return (
    permissions?.roles?.includes('dashboard') ||
    permissions?.canViewSales ||
    permissions?.canViewTransfer ||
    permissions?.canViewMarketing ||
    permissions?.canViewBrand ||
    permissions?.isAdmin ||
    false
  );
}

/**
 * Check if user has access to ANY sponsorship feature
 * Used for: Navigation tree filtering, page access, feature visibility
 *
 * Returns true if user has:
 * - Sponsors permission
 * - Packages permission
 * - Inventory permission
 * - Proposals permission
 * - Analytics permission
 * - Admin role
 */
export function hasAnySponsorshipPermission(
  permissions: PermissionsData | null | undefined
): boolean {
  return (
    permissions?.canViewSponsors ||
    permissions?.canViewPackages ||
    permissions?.canViewInventory ||
    permissions?.canViewProposals ||
    permissions?.canViewAnalytics ||
    permissions?.isAdmin ||
    false
  );
}

/**
 * Check if user has admin role (raw role array check)
 * Used for: API routes security checks
 *
 * @param roles - Array of role strings
 * @returns true if user has admin role
 */
export function isAdmin(roles: string[]): boolean {
  return roles.includes('admin');
}

/**
 * Check if permissions object indicates admin
 * Used for: Frontend components, permission checks
 *
 * @param permissions - Permissions object from usePermissionsQuery
 * @returns true if user is admin
 */
export function isAdminUser(permissions: PermissionsData | null | undefined): boolean {
  return permissions?.isAdmin === true;
}

// ============================================================================
// VALUATION SPECIFIC PERMISSIONS
// ============================================================================

/**
 * Check if user can view Dashboard
 * Used for: Dashboard page access, Dashboard menu item visibility
 */
export function canViewDashboard(permissions: PermissionsData | null | undefined): boolean {
  return permissions?.canViewDashboard || permissions?.isAdmin || false;
}

/**
 * Check if user can edit Dashboard
 * Used for: Dashboard configuration, customization
 */
export function canEditDashboard(permissions: PermissionsData | null | undefined): boolean {
  return permissions?.canEditDashboard || permissions?.isAdmin || false;
}

/**
 * Check if user can view Reports
 * Used for: Reports page access
 */
export function canViewReports(permissions: PermissionsData | null | undefined): boolean {
  return permissions?.canViewReports || permissions?.isAdmin || false;
}

/**
 * Check if user can edit Reports
 * Used for: Report configuration, export settings
 */
export function canEditReports(permissions: PermissionsData | null | undefined): boolean {
  return permissions?.canEditReports || permissions?.isAdmin || false;
}

/**
 * Check if user can view Methods
 * Used for: Methods page access
 */
export function canViewMethods(permissions: PermissionsData | null | undefined): boolean {
  return permissions?.canViewMethods || permissions?.isAdmin || false;
}

/**
 * Check if user can edit Methods
 * Used for: Methods configuration, formula editing
 */
export function canEditMethods(permissions: PermissionsData | null | undefined): boolean {
  return permissions?.canEditMethods || permissions?.isAdmin || false;
}

/**
 * Check if user can view Analytics (Reports & Methods)
 * Used for: Analytics menu items
 * @deprecated Use canViewReports() or canViewMethods() instead for specific pages
 */
export function canViewAnalytics(permissions: PermissionsData | null | undefined): boolean {
  return (
    permissions?.canViewReports ||
    permissions?.canViewMethods ||
    permissions?.canViewAnalytics ||
    permissions?.isAdmin ||
    false
  );
}

/**
 * Check if user can approve items (valuations, packages, proposals)
 * Used for: Approval actions across the platform
 * Checks the canApprove permission flag
 */
export function canApprove(permissions: PermissionsData | null | undefined): boolean {
  return permissions?.canApprove || permissions?.isAdmin || false;
}

/**
 * Check if user can approve valuations
 * Used for: Valuation approval actions
 * @deprecated Use canApprove() instead - both check the same permission
 */
export function canApproveValuations(permissions: PermissionsData | null | undefined): boolean {
  return permissions?.roles?.includes('approver') || permissions?.isAdmin || false;
}

/**
 * Check if user can edit ANY valuation section
 * Used for: Create/edit valuation pages
 *
 * Returns true if user can edit:
 * - Sales section
 * - Transfer section
 * - Marketing section
 * - Brand section
 * - Or is admin
 */
export function hasAnyEditPermission(permissions: PermissionsData | null | undefined): boolean {
  return (
    permissions?.canEditSales ||
    permissions?.canEditTransfer ||
    permissions?.canEditMarketing ||
    permissions?.canEditBrand ||
    permissions?.isAdmin ||
    false
  );
}

// ============================================================================
// SPONSORSHIP SPECIFIC PERMISSIONS
// ============================================================================

/**
 * Check if user can manage sponsors
 * Used for: Sponsors page, sponsorship analytics
 */
export function canManageSponsors(permissions: PermissionsData | null | undefined): boolean {
  return permissions?.canViewSponsors || permissions?.isAdmin || false;
}

/**
 * Check if user can manage packages
 * Used for: Packages page, package builder
 */
export function canManagePackages(permissions: PermissionsData | null | undefined): boolean {
  return permissions?.canViewPackages || permissions?.isAdmin || false;
}

/**
 * Check if user can manage item inventory
 * Used for: Item inventory page
 */
export function canManageInventory(permissions: PermissionsData | null | undefined): boolean {
  return permissions?.canViewInventory || permissions?.isAdmin || false;
}

/**
 * Check if user can manage proposals
 * Used for: Proposals page, create proposal page
 */
export function canManageProposals(permissions: PermissionsData | null | undefined): boolean {
  return permissions?.canViewProposals || permissions?.isAdmin || false;
}

// ============================================================================
// PLATFORM PERMISSIONS
// ============================================================================

/**
 * Check if user can view users page
 * Used for: Users page access check
 * Note: For edit operations, check canEditUsers() separately
 */
export function canManageUsers(permissions: PermissionsData | null | undefined): boolean {
  return permissions?.canViewUsers || permissions?.isAdmin || false;
}

/**
 * Check if user can edit users (invite, modify, delete)
 * Used for: User management form fields, invite button, etc.
 */
export function canEditUsers(permissions: PermissionsData | null | undefined): boolean {
  return permissions?.canEditUsers || permissions?.isAdmin || false;
}

/**
 * Check if user can view organization settings page
 * Used for: Organisation page access check
 * Note: For edit operations, check canEditOrganisation() separately
 */
export function canManageOrganisation(permissions: PermissionsData | null | undefined): boolean {
  return permissions?.canViewOrganisation || permissions?.isAdmin || false;
}

/**
 * Check if user can edit organization settings
 * Used for: Organization form fields, save buttons, etc.
 */
export function canEditOrganisation(permissions: PermissionsData | null | undefined): boolean {
  return permissions?.canEditOrganisation || permissions?.isAdmin || false;
}

/**
 * Check if user can view app settings page
 * Used for: Settings page access check
 * Note: For edit operations, check canEditAppSettings() separately
 */
export function canManageAppSettings(permissions: PermissionsData | null | undefined): boolean {
  return permissions?.canViewAppSettings || permissions?.isAdmin || false;
}

/**
 * Check if user can edit app settings (currency, brand defaults, regional settings)
 * Used for: App settings form fields, save buttons, etc.
 */
export function canEditAppSettings(permissions: PermissionsData | null | undefined): boolean {
  return permissions?.canEditAppSettings || permissions?.isAdmin || false;
}

/**
 * Check if user can manage either organization OR app settings
 * Used for: Backwards compatibility - checks both permissions
 * @deprecated Use canManageOrganisation() or canManageAppSettings() instead
 */
export function canManageSettings(permissions: PermissionsData | null | undefined): boolean {
  return (
    permissions?.canEditOrganisation ||
    permissions?.canEditAppSettings ||
    permissions?.isAdmin ||
    false
  );
}

/**
 * Check if user can view billing page
 * Used for: Billing page access check
 * Note: For edit operations, check canEditBilling() separately
 */
export function canManageBilling(permissions: PermissionsData | null | undefined): boolean {
  return permissions?.canViewBilling || permissions?.isAdmin || false;
}

/**
 * Check if user can edit billing (change plan, update payment)
 * Used for: Billing form fields, upgrade buttons, etc.
 */
export function canEditBilling(permissions: PermissionsData | null | undefined): boolean {
  return permissions?.canEditBilling || permissions?.isAdmin || false;
}

/**
 * Check if user can view plugins page
 * Used for: Plugins page access check
 * Note: For edit operations, check canEditPlugins() separately
 */
export function canManagePlugins(permissions: PermissionsData | null | undefined): boolean {
  return permissions?.canViewPlugins || permissions?.isAdmin || false;
}

/**
 * Check if user can edit plugins (install, configure, uninstall)
 * Used for: Plugin installation buttons, configuration forms, etc.
 */
export function canEditPlugins(permissions: PermissionsData | null | undefined): boolean {
  return permissions?.canEditPlugins || permissions?.isAdmin || false;
}

/**
 * Check if user can view API page
 * Used for: API management page access check
 * Note: For edit operations, check canEditAPI() separately
 */
export function canManageAPI(permissions: PermissionsData | null | undefined): boolean {
  // canViewAPI is plan-aware (planHasAPI && (isAdmin || hasAPIRole)) — no isAdmin
  // fallback here, or admins on plans without API access would see the page
  return permissions?.canViewAPI || false;
}

/**
 * Check if user can edit API (create, delete API keys)
 * Used for: API key creation, deletion buttons, etc.
 */
export function canEditAPI(permissions: PermissionsData | null | undefined): boolean {
  return permissions?.canEditAPI || permissions?.isAdmin || false;
}

/**
 * Check if user can view audit logs
 * Used for: Audit log page
 */
export function canViewAuditLogs(permissions: PermissionsData | null | undefined): boolean {
  return (
    permissions?.canViewAuditLog ||
    permissions?.roles?.includes('audit_log') ||
    permissions?.isAdmin ||
    false
  );
}

/**
 * Check if user can view SSO settings
 * Used for: SSO configuration page access check
 * Note: For edit operations, check canEditSSO() separately
 */
export function canManageSSO(permissions: PermissionsData | null | undefined): boolean {
  return permissions?.canViewSSO || permissions?.isAdmin || false;
}

/**
 * Check if user can edit SSO settings
 * Used for: SSO configuration form fields, save buttons, etc.
 */
export function canEditSSO(permissions: PermissionsData | null | undefined): boolean {
  return permissions?.canEditSSO || permissions?.isAdmin || false;
}

/**
 * Check if user can view documents page
 * Used for: Documents page access check
 * Note: For edit operations, check canEditDocuments() separately
 */
export function canManageDocuments(permissions: PermissionsData | null | undefined): boolean {
  return permissions?.canViewDocuments || permissions?.isAdmin || false;
}

/**
 * Check if user can edit documents (upload, delete, modify)
 * Used for: Document upload buttons, delete buttons, edit forms, etc.
 */
export function canEditDocuments(permissions: PermissionsData | null | undefined): boolean {
  return permissions?.canEditDocuments || permissions?.isAdmin || false;
}

// ============================================================================
// GENERIC HELPER
// ============================================================================

/**
 * Check if user has a specific role
 * Used for: Generic role checking when no specific helper exists
 *
 * @param permissions - User permissions data
 * @param role - Role to check for (e.g., 'dashboard', 'analytics', 'user_management')
 */
export function hasRole(permissions: PermissionsData | null | undefined, role: string): boolean {
  return permissions?.roles?.includes(role) || permissions?.isAdmin || false;
}
